/*!
 * Fitting Finder — vanilla JS, no dependencies.
 * Part of: sections/fitting-finder.liquid, assets/fitting-finder.css
 *
 * Data: the section renders every product referenced by a "Fitting" metaobject
 * entry (Unlisted products included) as JSON, live, on each page load:
 * fittings (custom.fitting_type_N / custom.fitting_size_N), price, stock, variants.
 *
 * The shopper picks ends one at a time (type, then size). Ends are matched no
 * matter which metafield slot (1–3) holds them, so "NPT 1/4in" then
 * "BARB 1.25IN" finds a product stored as BARB/1.25IN, BARB/1.25IN, NPT/1/4in.
 */
(function () {
  'use strict';

  var MAX_STEPS = 3;
  var PER_PAGE_OPTIONS = [20, 40, 80, 100];
  var DEFAULT_PER_PAGE = 20;
  var NO_SIZE = '__none__'; // size value for ends stored without a size (e.g. PCV Valve)
  var NONE_TYPE = '__none_type__'; // Type 3 choice meaning "no third end"
  var BARB = 'BARB';        // normalized type that shows two size fields: SAE (size A) + Metric (size B)

  /* ---------- helpers ---------- */

  // Comparison key: case/space-insensitive. "1/4 in" == "1/4IN"
  function norm(v) {
    return String(v == null ? '' : v).trim().toUpperCase().replace(/\s+/g, '');
  }
  function endKey(t, s, sb) { return norm(t) + '|' + norm(s) + '|' + norm(sb); }

  // A Type metafield can list more than one type for the same end, separated by
  // a comma: "ORB, AN FLARE" is one end that is both ORB and AN Flare, so it
  // matches either one. Only Type is split — sizes are left alone.
  var TYPE_SEPARATOR = /,/;
  function splitTypes(t) {
    return String(t == null ? '' : t).split(TYPE_SEPARATOR)
      .map(function (x) { return x.trim(); })
      .filter(function (x) { return norm(x); });
  }

  // Rough numeric value of a size string for sorting ("1/4in", "1-1/4", "1.25IN", "M12x1.5")
  function sizeValue(s) {
    var str = String(s).toUpperCase();
    var mixed = str.match(/(\d+)\s*[- ]\s*(\d+)\s*\/\s*(\d+)/);
    if (mixed) return +mixed[1] + mixed[2] / mixed[3];
    var frac = str.match(/(\d+)\s*\/\s*(\d+)/);
    if (frac) return frac[1] / frac[2];
    var num = str.match(/\d*\.?\d+/);
    return num ? parseFloat(num[0]) : Infinity;
  }
  function bySize(a, b) {
    var d = sizeValue(a.label) - sizeValue(b.label);
    if (d !== 0 && !isNaN(d)) return d;
    return a.label.localeCompare(b.label, undefined, { numeric: true });
  }
  function byLabel(a, b) {
    return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
  }
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'text') node.textContent = attrs[k];
      else if (k === 'class') node.className = attrs[k];
      else node.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) { if (c) node.appendChild(c); });
    return node;
  }

  /* ---------- data ---------- */

  function parseBlock(script) {
    var list;
    try { list = JSON.parse(script.textContent); } catch (e) {
      console.error('[Fitting Finder] Could not read product data', e);
      return [];
    }
    return (list || []).map(function (p) {
      // All product images for the enlarged view (falls back to the featured image).
      p.gallery = (p.images || []).filter(function (g) { return g && g.l; });
      if (!p.gallery.length && p.image) p.gallery = [{ t: p.image, l: p.imageLarge || p.image, alt: p.title }];
      p.ends = (p.fittings || [])
        .filter(function (e) { return norm(e.t); })
        .map(function (e) {
          var labels = splitTypes(e.t);
          return {
            t: labels.join(', '),                          // shown on the card
            types: labels.map(norm),                       // each one matches
            typeLabels: labels,                            // each one is a dropdown option
            s: e.s || '',                                  // size A (BARB: SAE, inches)
            sb: e.sb || '',                                // size B (BARB: Metric, mm)
            key: endKey(e.t, e.s, e.sb)
          };
        });
      return p;
    }).filter(function (p) { return p.ends.length >= 2; });
  }

  // Page 1 is embedded in the section. If there are more than 250 Fitting
  // entries, fetch the remaining pages of this section via the Section Rendering API.
  function loadProducts(root) {
    var first = root.querySelector('script[data-ff-data]');
    if (!first) return Promise.resolve([]);
    var products = parseBlock(first);
    var pages = parseInt(first.getAttribute('data-pages'), 10) || 1;
    if (pages <= 1) return Promise.resolve(products);

    var sectionId = root.getAttribute('data-section-id');
    var reqs = [];
    for (var n = 2; n <= pages; n++) {
      var url = window.location.pathname + '?section_id=' + encodeURIComponent(sectionId) + '&page=' + n;
      reqs.push(fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var s = doc.querySelector('script[data-ff-data]');
          return s ? parseBlock(s) : [];
        })
        .catch(function () { return []; }));
    }
    return Promise.all(reqs).then(function (rest) {
      var seen = {};
      return products.concat.apply(products, rest).filter(function (p) {
        if (seen[p.id]) return false;
        seen[p.id] = true;
        return true;
      });
    });
  }

  function addToCart(addUrl, variantId) {
    return fetch(addUrl.replace(/\/?$/, '') + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw new Error(body.description || body.message || 'Could not add to cart');
        return body;
      });
    });
  }

  // After an add, update the theme's cart UI the same way its own add-to-cart does:
  //  - window.MiniCartPopup (assets/mini-cart-popup.js): header count + "added" popup
  //  - publish(PUB_SUB_EVENTS.cartUpdate) (pubsub.js / constants.js): cart drawer refresh
  // Falls back to re-rendering the cart-icon-bubble section if neither exists.
  function refreshThemeCart(cartUrl) {
    var base = (cartUrl || '/cart').replace(/\/$/, '');
    return fetch(base + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var handled = false;
        var mcp = window.MiniCartPopup;
        if (mcp) {
          try { if (mcp.updateHeaderBadges) mcp.updateHeaderBadges(cart.item_count); handled = true; } catch (e) { /* ignore */ }
          try { if (mcp.open) mcp.open(null, cart); } catch (e) { /* ignore */ }
        }
        // PUB_SUB_EVENTS is a top-level const, so it is not on window; reach it by name.
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fitting-finder', cart: cart });
            handled = true;
          }
        } catch (e) { /* ignore */ }

        if (!handled) {
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble) {
            fetch(window.location.pathname + '?sections=cart-icon-bubble', { credentials: 'same-origin' })
              .then(function (r) { return r.json(); })
              .then(function (sections) {
                var html = sections && sections['cart-icon-bubble'];
                if (!html) return;
                var doc = new DOMParser().parseFromString(html, 'text/html');
                var fresh = doc.querySelector('.shopify-section') || doc.body;
                bubble.innerHTML = fresh.innerHTML;
              })
              .catch(function () { /* ignore */ });
          }
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
        return cart;
      });
  }

  /* ---------- matching ---------- */

  // A selected end is { type, size }. size is null when the shopper skipped it
  // (any size matches), '' for "Not required", otherwise a normalized size.
  function endMatches(end, sel) {
    if (end.types.indexOf(sel.type) === -1) return false;
    if (sel.sizeB && norm(end.sb) !== sel.sizeB) return false; // BARB Metric (size B)
    return sel.size === null || norm(end.s) === sel.size;
  }

  // Every possible set of ends left over after pairing each selected end with a
  // different end of the product. Because size can be skipped, one selection may
  // fit several ends, so all pairings are tried (at most 3 ends, so this is tiny).
  // Returns [] if the product doesn't contain all the selected ends.
  function remainingEndSets(product, selected) {
    var out = [];
    (function walk(i, left) {
      if (i === selected.length) { out.push(left); return; }
      for (var j = 0; j < left.length; j++) {
        if (endMatches(left[j], selected[i])) {
          walk(i + 1, left.slice(0, j).concat(left.slice(j + 1)));
        }
      }
    })(0, product.ends.slice());
    return out;
  }
  function remainingEnds(product, selected) {
    var sets = remainingEndSets(product, selected);
    return sets.length ? sets[0] : null;
  }

  // m.left  = ends that could still be chosen next (from any pairing)
  // m.exact = some pairing uses up every end of the product
  function matches(products, selected) {
    var out = [];
    products.forEach(function (p) {
      var sets = remainingEndSets(p, selected);
      if (!sets.length) return;
      var left = [], seen = {};
      sets.forEach(function (set) {
        set.forEach(function (e) {
          if (seen[e.key]) return;
          seen[e.key] = true;
          left.push(e);
        });
      });
      out.push({ product: p, left: left, exact: sets.some(function (set) { return !set.length; }) });
    });
    return out;
  }

  // Type options: each type of a multi-type end ("ORB, AN FLARE") is its own option.
  function typeOptions(ends) {
    var seen = {}, list = [];
    ends.forEach(function (e) {
      e.typeLabels.forEach(function (label) {
        var k = norm(label);
        if (!k || seen[k]) return;
        seen[k] = true;
        list.push({ value: k, label: label });
      });
    });
    return list;
  }

  // Unique option list from a set of ends; first-seen spelling is the label.
  function uniqueOptions(ends, field) {
    var seen = {}, list = [];
    ends.forEach(function (e) {
      var k = norm(e[field]);
      if (!k || seen[k]) return;
      seen[k] = true;
      list.push({ value: k, label: String(e[field]).trim() });
    });
    return list;
  }

  // Is there an end with this size A (SAE) and size B (Metric)? Either may be '' (any).
  function hasEnd(ends, a, b) {
    return ends.some(function (e) {
      return (!a || (a === NO_SIZE ? !norm(e.s) : norm(e.s) === a)) && (!b || norm(e.sb) === b);
    });
  }
  // The single value of wantField among ends whose knownField equals known, or ''
  // when there are several (or some of those ends have none).
  function onlyPartner(ends, knownField, known, wantField) {
    var rows = ends.filter(function (e) { return norm(e[knownField]) === known; });
    var vals = {}, count = 0, allSet = rows.length > 0, only = '';
    rows.forEach(function (e) {
      var w = norm(e[wantField]);
      if (!w) { allSet = false; return; }
      if (!vals[w]) { vals[w] = 1; count++; only = w; }
    });
    return (allSet && count === 1) ? only : '';
  }

  // Card order: ends that match the shopper's sides come first, in side order
  // (Side 1's pick first, then Side 2's, ...); any remaining ends follow in their stored order.
  function orderEnds(ends, selected) {
    if (!selected || !selected.length) return ends;
    var best = null;
    (function walk(i, used, picked) {
      if (best) return;
      if (i === selected.length) { best = picked; return; }
      for (var j = 0; j < ends.length; j++) {
        if (used.indexOf(j) === -1 && endMatches(ends[j], selected[i])) {
          walk(i + 1, used.concat(j), picked.concat(j));
          if (best) return;
        }
      }
    })(0, [], []);
    if (!best) return ends;
    var out = best.map(function (j) { return ends[j]; });
    ends.forEach(function (e, j) { if (best.indexOf(j) === -1) out.push(e); });
    return out;
  }

  /* ---------- UI ---------- */

  function FittingFinder(root) {
    this.root = root;
    this.stepsEl = root.querySelector('[data-ff-steps]');
    this.statusEl = root.querySelector('[data-ff-status]');
    this.resultsEl = root.querySelector('[data-ff-results]');
    this.resetBtn = root.querySelector('[data-ff-reset]');
    this.uid = 'ff' + Math.random().toString(36).slice(2, 8);
    var d = root.dataset;
    this.text = {
      sideHeading: d.textSideHeading || 'Side [n] Connection',
      typeLabel: d.textTypeField || 'Type',
      sizeLabel: d.textSizeField || 'Size',
      typePh: d.textTypePlaceholder || 'Select a type',
      sizePh: d.textAnySize || 'Any size',
      none: d.textNoResults || 'No products match these fittings.',
      exact: d.textExact || 'Exact match',
      add: d.textAdd || 'Add to cart',
      soldOut: d.textOutOfStock || 'Out of stock',
      noSize: d.textNoSize || 'Not required',
      noneLabel: d.textNone || 'None',
      sae: d.textSae || 'SAE',
      metric: d.textMetric || 'Metric',
      barbNote: d.textBarbNote || 'Filter by SAE or Metric',
      enlarge: d.textEnlarge || 'Enlarge image',
      zoomIn: d.textZoomIn || 'Zoom in',
      zoomOut: d.textZoomOut || 'Zoom out',
      close: d.textClose || 'Close',
      prevImage: d.textPrevImage || 'Previous image',
      nextImage: d.textNextImage || 'Next image'
    };
    this.showDescription = d.showDescription !== 'false';
    this.showPartial = d.showPartial !== 'false';
    this.cartUrl = d.cartUrl || '/cart';
    this.cartAddUrl = d.cartAddUrl || '/cart/add';
    var per = parseInt(d.perPage, 10);
    this.perPage = PER_PAGE_OPTIONS.indexOf(per) !== -1 ? per : DEFAULT_PER_PAGE;
    this.page = 1;
    this.found = null;
    this.products = [];
    this.picks = []; // [{ type: 'NPT', size: '1/4IN' }] normalized values
    this.buyCount = 0; // keeps option-picker ids unique

    var self = this;
    this.resetBtn.addEventListener('click', function () {
      self.picks = [];
      self.render();
      var first = self.stepsEl.querySelector('select');
      if (first) first.focus();
    });

    loadProducts(root).then(function (products) {
      self.products = products;
      if (!products.length) {
        self.setStatus('No fittings found. Add products under Content → Metaobjects → Fitting.');
        return;
      }
      self.render();
    }).catch(function (err) {
      console.error('[Fitting Finder]', err);
      self.setStatus('Sorry, the fitting finder could not load. Please refresh the page.');
    });
  }

  FittingFinder.prototype.setStatus = function (msg) {
    this.statusEl.textContent = msg || '';
    this.statusEl.hidden = !msg;
  };

  FittingFinder.prototype.selectedEnds = function (count) {
    var out = [];
    for (var i = 0; i < count; i++) {
      var p = this.picks[i];
      if (!p || !p.type || p.type === NONE_TYPE) break;
      out.push({ type: p.type, size: p.size === NO_SIZE ? '' : (p.size || null), sizeB: p.sizeB || null });
    }
    return out;
  };

  FittingFinder.prototype.label = function (tpl, n) {
    return tpl.replace(/\[n\]/g, n);
  };

  FittingFinder.prototype.buildSelect = function (id, labelText, placeholder, options, value, onChange, opts) {
    opts = opts || {};
    var select = el('select', { id: id, class: 'fitting-finder__select' });
    var ph = el('option', { value: '', text: placeholder });
    // "None" is only shown, never offered as a choice.
    if (opts.placeholderLocked) { ph.disabled = true; ph.hidden = true; ph.selected = true; }
    select.appendChild(ph);
    options.forEach(function (o) {
      var opt = el('option', { value: o.value, text: o.label });
      if (o.value === value) opt.selected = true;
      select.appendChild(opt);
    });
    if (opts.disabled) select.disabled = true;
    select.addEventListener('change', function () { onChange(select.value); });
    return el('div', { class: 'fitting-finder__field', 'data-step': opts.step || '' }, [
      el('label', { for: id, class: 'fitting-finder__label', text: labelText }),
      el('div', { class: 'fitting-finder__select-wrap' }, [select])
    ]);
  };

  // Sides 1 and 2 (Type + Size) always show (disabled until the type before
  // them is chosen). Size is optional: choosing just a type unlocks the next
  // step and filters by that type in any size. Type/Size 3 only shows once
  // types 1 and 2 are chosen AND at least one matching product has a third end.
  FittingFinder.prototype.render = function () {
    var self = this;
    this.stepsEl.innerHTML = '';
    this.setStatus('');

    var ALWAYS_SHOWN = 2;
    var lastComplete = 0;

    for (var i = 0; i < MAX_STEPS; i++) {
      var prior = this.selectedEnds(i);
      var enabled = prior.length === i; // every earlier type chosen

      var leftEnds = [];
      if (enabled) {
        matches(this.products, prior).forEach(function (m) { leftEnds = leftEnds.concat(m.left); });
      }
      if (i >= ALWAYS_SHOWN && (!enabled || !leftEnds.length)) break;

      var pick = this.picks[i] || (this.picks[i] = { type: '', size: '', sizeB: '' });
      var typeOpts = typeOptions(leftEnds).sort(byLabel);
      // Side 3 also offers "None": fittings that have only the two ends already chosen.
      if (i >= ALWAYS_SHOWN && enabled && matches(this.products, prior).some(function (m) { return m.exact; })) {
        typeOpts.unshift({ value: NONE_TYPE, label: this.text.noneLabel });
      }

      if (pick.type && !typeOpts.some(function (o) { return o.value === pick.type; })) {
        pick.type = ''; pick.size = ''; pick.sizeB = ''; pick.autoSize = pick.autoSizeB = false;
      }
      // BARB has two linked sizes: SAE (inches, size A) and Metric (mm, size B).
      var isBarb = pick.type === BARB;

      var n = i + 1;

      // Each side: an H3 ("Side 1 Connection") above its Type and Size fields.
      // On desktop sides 1 and 2 sit side by side; side 3 starts a new row.
      // Two fields per side: Type and Size. BARB's Size lists both its size A (SAE, inches) and size B (Metric, mm) values.
      var fieldsEl = el('div', { class: 'fitting-finder__side-fields' });
      this.stepsEl.appendChild(el('div', {
        class: 'fitting-finder__side' + (i >= ALWAYS_SHOWN ? ' fitting-finder__side--new-row' : ''),
        'data-side': n
      }, [
        el('h3', { class: 'fitting-finder__side-heading', text: this.label(this.text.sideHeading, n) }),
        fieldsEl
      ]));

      fieldsEl.appendChild(this.buildSelect(
        this.uid + '-type-' + n, this.label(this.text.typeLabel, n), this.text.typePh,
        typeOpts, pick.type,
        (function (idx) {
          return function (val) {
            self.picks = self.picks.slice(0, idx);
            self.picks[idx] = { type: val, size: '', sizeB: '' };
            self.render();
            self.focusStep(idx + 1, val ? 'size' : 'type');
          };
        })(i),
        { step: n, disabled: !enabled || !typeOpts.length }
      ));

      var sizeOpts = [];
      var metricOpts = [];
      var sizeNotRequired = false;
      var barbOpts = [];
      var barbValue = '';
      var typeEnds = [];
      if (enabled && pick.type) {
        typeEnds = leftEnds.filter(function (e) { return e.types.indexOf(pick.type) !== -1; });
        // Both lists always offer every size of this type, so either field can be changed at any time.
        sizeOpts = uniqueOptions(typeEnds, 's').sort(bySize);
        // Types stored without a size (e.g. "PCV Valve") get a "Not required" choice.
        if (typeEnds.some(function (e) { return !norm(e.s); })) {
          sizeOpts.unshift({ value: NO_SIZE, label: this.text.noSize });
        }
        if (isBarb) {
          metricOpts = uniqueOptions(typeEnds, 'sb').sort(bySize);
        }
        if (pick.size && !sizeOpts.some(function (o) { return o.value === pick.size; })) pick.size = '';
        if (pick.sizeB && !metricOpts.some(function (o) { return o.value === pick.sizeB; })) pick.sizeB = '';
        if (pick.size && pick.sizeB && !hasEnd(typeEnds, pick.size, pick.sizeB)) pick.sizeB = '';
        if (!isBarb && !pick.size && sizeOpts.length === 1) { pick.size = sizeOpts[0].value; pick.autoSize = true; }
        sizeNotRequired = !isBarb && sizeOpts.length === 1 && sizeOpts[0].value === NO_SIZE;
        if (pick.type === NONE_TYPE) sizeNotRequired = true; // "None" has no size
        // BARB: one Size dropdown with every size A value, then every size B value ('a:' / 'b:' tells them apart).
        if (isBarb) {
          barbOpts = sizeOpts.map(function (o) { return { value: 'a:' + o.value, label: o.label }; })
            .concat(metricOpts.map(function (o) { return { value: 'b:' + o.value, label: o.label }; }));
          barbValue = pick.sizeB ? 'b:' + pick.sizeB : (pick.size ? 'a:' + pick.size : '');
        }
      } else {
        pick.size = '';
        pick.sizeB = '';
      }

      if (isBarb) {
        fieldsEl.appendChild(this.buildSelect(
          this.uid + '-size-' + n, this.label(this.text.sizeLabel, n), this.text.sizePh,
          barbOpts, barbValue,
          (function (idx) {
            return function (val) {
              var pk = self.picks[idx];
              pk.autoSize = pk.autoSizeB = false;
              pk.size = val.indexOf('a:') === 0 ? val.slice(2) : '';
              pk.sizeB = val.indexOf('b:') === 0 ? val.slice(2) : '';
              self.render();
              self.focusStep(idx + 2, 'type');
            };
          })(i),
          { step: n, disabled: !enabled || !pick.type || !barbOpts.length }
        ));
      } else {
        fieldsEl.appendChild(this.buildSelect(
          this.uid + '-size-' + n, this.label(this.text.sizeLabel, n), this.text.sizePh,
          sizeOpts, pick.size,
          (function (idx) {
            return function (val) {
              var pk = self.picks[idx];
              pk.size = val; pk.autoSize = false;
              self.render();
              self.focusStep(idx + 2, 'type');
            };
          })(i),
          { step: n, disabled: !enabled || !pick.type || sizeNotRequired }
        ));
      }

      if (enabled && pick.type) lastComplete = n;
    }

    this.picks = this.picks.slice(0, Math.max(lastComplete + 1, 1));
    this.resetBtn.hidden = !(this.picks[0] && this.picks[0].type);
    this.renderResults(lastComplete);
  };

  // Focus the next field. If it's a size that isn't required (greyed out),
  // skip ahead to the next type instead.
  FittingFinder.prototype.focusStep = function (n, which) {
    var s = document.getElementById(this.uid + '-' + which + '-' + n);
    if (s && s.disabled && which === 'size') s = document.getElementById(this.uid + '-type-' + (n + 1));
    if (s && !s.disabled) s.focus();
  };

  // Builds the result list for the current selections, then shows page 1.
  // With nothing selected, every fitting is listed (alphabetically).
  FittingFinder.prototype.renderResults = function (complete) {
    var found;
    if (!complete) {
      found = this.products.map(function (p) { return { product: p, exact: false }; });
    } else {
      var last = this.picks[complete - 1];
      var noneChosen = !!(last && last.type === NONE_TYPE);
      found = matches(this.products, this.selectedEnds(complete));
      // "None" for Type 3: only fittings with exactly the ends chosen so far.
      if (noneChosen) found = found.filter(function (m) { return m.exact; });
      var moreToChoose = found.some(function (m) { return !m.exact; });
      if (!this.showPartial && moreToChoose) found = null; // setting: wait until every end is chosen
    }
    if (found) {
      found.sort(function (a, b) {
        return (b.exact - a.exact) ||
          a.product.title.localeCompare(b.product.title, undefined, { numeric: true });
      });
    }
    this.found = found;
    this.cardSel = complete ? this.selectedEnds(complete) : [];
    this.page = 1;
    this.drawResults(false);
  };

  // The results bar ("Showing 1–20 of 209 products" + "Show [20] per page") is
  // built once and kept. Only its count text changes, so the per-page dropdown
  // is never replaced or refocused — which is what made it pop open again.
  FittingFinder.prototype.ensureResultsLayout = function () {
    if (this.barEl && this.barEl.parentNode === this.resultsEl) return;
    var self = this;
    this.resultsEl.innerHTML = '';

    var perId = this.uid + '-per-page';
    var perSel = el('select', { id: perId, class: 'fitting-finder__select fitting-finder__per-page-select' });
    PER_PAGE_OPTIONS.forEach(function (n) {
      perSel.appendChild(el('option', { value: String(n), text: String(n) }));
    });
    perSel.addEventListener('change', function () {
      var firstShown = (self.page - 1) * self.perPage; // keep the first visible item on screen
      self.perPage = parseInt(perSel.value, 10) || DEFAULT_PER_PAGE;
      self.page = Math.floor(firstShown / self.perPage) + 1;
      self.drawResults(false);
    });

    this.countEl = el('p', { class: 'fitting-finder__count' });
    this.perSelEl = perSel;
    this.barEl = el('div', { class: 'fitting-finder__results-bar' }, [
      this.countEl,
      el('div', { class: 'fitting-finder__per-page' }, [
        el('label', { for: perId, text: 'Show' }),
        el('div', { class: 'fitting-finder__select-wrap' }, [perSel]),
        el('span', { text: 'per page' })
      ])
    ]);
    this.bodyEl = el('div', { class: 'fitting-finder__results-body' });
    this.resultsEl.appendChild(this.barEl);
    this.resultsEl.appendChild(this.bodyEl);
  };

  // Draws one page of this.found plus pagination.
  FittingFinder.prototype.drawResults = function (scroll) {
    var self = this;
    this.ensureResultsLayout();
    this.bodyEl.innerHTML = '';
    var found = this.found;

    if (!found || !found.length) {
      this.barEl.hidden = true;
      if (found) this.bodyEl.appendChild(el('p', { class: 'fitting-finder__empty', text: this.text.none }));
      return;
    }
    this.barEl.hidden = false;

    var total = found.length;
    var per = this.perPage;
    var pages = Math.max(1, Math.ceil(total / per));
    if (this.page > pages) this.page = pages;
    var start = (this.page - 1) * per;
    var end = Math.min(start + per, total);

    this.countEl.textContent = 'Showing ' + (start + 1) + '–' + end + ' of ' + total +
      (total === 1 ? ' product' : ' products');
    if (this.perSelEl.value !== String(per)) this.perSelEl.value = String(per);

    var list = el('ul', { class: 'fitting-finder__grid', role: 'list' });
    found.slice(start, end).forEach(function (m) {
      list.appendChild(el('li', { class: 'fitting-finder__item' }, [self.buildCard(m.product, m.exact)]));
    });
    this.bodyEl.appendChild(list);

    if (pages > 1) this.bodyEl.appendChild(this.buildPager(pages));
    if (scroll && this.resultsEl.scrollIntoView) {
      this.resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  FittingFinder.prototype.goToPage = function (n) {
    this.page = n;
    this.drawResults(true);
  };

  // Prev · 1 … 4 5 6 … 11 · Next
  FittingFinder.prototype.buildPager = function (pages) {
    var self = this;
    var cur = this.page;
    var nav = el('nav', { class: 'fitting-finder__pager', 'aria-label': 'Fitting results pages' });
    var ul = el('ul', { class: 'fitting-finder__pager-list', role: 'list' });

    function btn(label, page, opts) {
      opts = opts || {};
      var b = el('button', {
        type: 'button',
        class: 'fitting-finder__page' + (opts.current ? ' is-current' : ''),
        'aria-label': opts.aria || ('Page ' + page)
      });
      b.textContent = label;
      if (opts.current) b.setAttribute('aria-current', 'page');
      if (opts.disabled) b.disabled = true;
      else if (!opts.current) b.addEventListener('click', function () { self.goToPage(page); });
      ul.appendChild(el('li', null, [b]));
    }
    function gap() { ul.appendChild(el('li', { class: 'fitting-finder__page-gap', 'aria-hidden': 'true', text: '…' })); }

    btn('‹ Prev', cur - 1, { aria: 'Previous page', disabled: cur <= 1 });
    var nums = [];
    for (var p = 1; p <= pages; p++) {
      if (p === 1 || p === pages || Math.abs(p - cur) <= 1) nums.push(p);
    }
    var last = 0;
    nums.forEach(function (p) {
      if (p - last > 1) gap();
      btn(String(p), p, { current: p === cur });
      last = p;
    });
    btn('Next ›', cur + 1, { aria: 'Next page', disabled: cur >= pages });

    nav.appendChild(ul);
    return nav;
  };

  FittingFinder.prototype.buildCard = function (p, exact) {
    var self = this;
    var variants = p.variants || [];
    var current = variants.filter(function (v) { return v.available; })[0] || variants[0] || null;

    var img = p.image
      ? el('img', { src: p.image, alt: p.title, loading: 'lazy', width: '400', height: '400' })
      : el('span', { class: 'fitting-finder__noimg', 'aria-hidden': 'true' });
    // The image opens a larger view (no link to the product page).
    var media;
    if (p.image) {
      media = el('button', {
        type: 'button',
        class: 'fitting-finder__media fitting-finder__media--zoom',
        'aria-label': this.text.enlarge + ': ' + p.title,
        'aria-haspopup': 'dialog'
      }, [img, el('span', { class: 'fitting-finder__zoom-hint', 'aria-hidden': 'true' })]);
      // More than one image: a small thumbnail of the next image with a "+N" count.
      // Clicking it opens the viewer on that image.
      var extra = p.gallery.length - 1;
      var moreEl = null;
      if (extra > 0) {
        moreEl = el('span', { class: 'fitting-finder__more', 'aria-hidden': 'true' }, [
          el('img', { src: p.gallery[1].t, alt: '', loading: 'lazy', width: '80', height: '80' }),
          el('span', { class: 'fitting-finder__more-count', text: '+' + extra })
        ]);
        media.appendChild(moreEl);
        media.setAttribute('aria-label', this.text.enlarge + ': ' + p.title + ' (' + p.gallery.length + ' images)');
      }
      media.addEventListener('click', function (e) {
        var onMore = moreEl && (e.target === moreEl || moreEl.contains(e.target));
        self.openViewer(p, media, onMore ? 1 : 0);
      });
    } else {
      media = el('div', { class: 'fitting-finder__media' }, [img]);
    }

    var badges = el('div', { class: 'fitting-finder__badges' });
    if (exact) badges.appendChild(el('span', { class: 'fitting-finder__badge', text: this.text.exact }));
    if (!p.available) badges.appendChild(el('span', { class: 'fitting-finder__badge fitting-finder__badge--muted', text: this.text.soldOut }));

    var ends = el('ul', { class: 'fitting-finder__ends', role: 'list' });
    orderEnds(p.ends, this.cardSel).forEach(function (e) {
      var type = String(e.t).trim();
      ends.appendChild(el('li', { text: type + (e.s ? ' ' + String(e.s).trim() : '') }));
      // Size B (BARB metric) gets its own bubble: "BARB 1/4IN" + "BARB 6.5mm".
      if (e.sb) ends.appendChild(el('li', { text: type + ' ' + String(e.sb).trim() }));
    });

    var price = el('span', { class: 'fitting-finder__price', text: current ? current.price : '' });

    var link = el('div', { class: 'fitting-finder__body' }, [
      media,
      el('div', { class: 'fitting-finder__info' }, [badges, el('span', { class: 'fitting-finder__title', text: p.title }), ends, price])
    ]);

    var buy = this.buildBuy(p, price);

    return el('div', { class: 'fitting-finder__card' }, [link, buy]);
  };

  // Option picker + Add to cart. Used on the product card and in the image viewer.
  // priceEl (optional) is updated when the option changes; onAdded runs after a
  // successful add.
  FittingFinder.prototype.buildBuy = function (p, priceEl, onAdded) {
    var self = this;
    var variants = p.variants || [];
    var current = variants.filter(function (v) { return v.available; })[0] || variants[0] || null;
    var buy = el('div', { class: 'fitting-finder__buy' });
    if (!current) return buy;

    var btn = el('button', { type: 'button', class: 'fitting-finder__buy-btn button button--primary' });

    var refresh = function () {
      btn.textContent = current.available ? self.text.add : self.text.soldOut;
      btn.disabled = !current.available;
      if (priceEl) priceEl.textContent = current.price || '';
    };

    if (variants.length > 1) {
      var vid = this.uid + '-v-' + p.id + '-' + (++this.buyCount);
      var sel = el('select', { id: vid, class: 'fitting-finder__select fitting-finder__variant' });
      variants.forEach(function (v) {
        var o = el('option', { value: String(v.id), text: v.title + (v.available ? '' : ' — ' + self.text.soldOut) });
        if (v.id === current.id) o.selected = true;
        sel.appendChild(o);
      });
      sel.addEventListener('change', function () {
        current = variants.filter(function (v) { return String(v.id) === sel.value; })[0] || current;
        refresh();
      });
      buy.appendChild(el('label', { for: vid, class: 'fitting-finder__visually-hidden', text: 'Option' }));
      buy.appendChild(sel);
    }

    btn.addEventListener('click', function () {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      addToCart(self.cartAddUrl, current.id).then(function () {
        btn.removeAttribute('aria-busy');
        refresh();
        if (onAdded) onAdded();
        // Update the header count, "added" popup and cart drawer without a reload.
        return refreshThemeCart(self.cartUrl).catch(function (e) {
          console.warn('[Fitting Finder] Added, but could not refresh the cart display', e);
        });
      }, function (err) {
        // Briefly show the reason (e.g. not enough stock) on the button, then restore it.
        btn.removeAttribute('aria-busy');
        btn.textContent = err.message;
        setTimeout(refresh, 3000);
      });
    });

    refresh();
    buy.appendChild(btn);
    return buy;
  };

  /* ---------- image viewer ---------- */

  var ZOOM_STEPS = [1, 1.5, 2, 3, 4];

  // Width of the image when it fits the viewer (the 100% zoom level).
  function fittedWidth(v) {
    var w = v.stage.clientWidth, h = v.stage.clientHeight;
    var nw = v.img.naturalWidth, nh = v.img.naturalHeight;
    if (nw && nh && w && h) return nw * Math.min(1, w / nw, h / nh); // never upscaled at 100%
    return v.img.offsetWidth || w;
  }

  // One viewer per finder, added to <body> the first time an image is opened.
  FittingFinder.prototype.buildViewer = function () {
    var self = this;
    var t = this.text;
    var titleId = this.uid + '-viewer-title';

    function tool(cls, label, symbol) {
      return el('button', { type: 'button', class: 'ff-viewer__btn ' + cls, 'aria-label': label, title: label },
        [el('span', { 'aria-hidden': 'true', text: symbol })]);
    }
    var zoomOut = tool('ff-viewer__zoom-out', t.zoomOut, '−');
    var zoomIn = tool('ff-viewer__zoom-in', t.zoomIn, '+');
    var closeBtn = tool('ff-viewer__close', t.close, '×');
    var level = el('span', { class: 'ff-viewer__level', 'aria-live': 'polite' });
    var count = el('span', { class: 'ff-viewer__count', 'aria-live': 'polite' });
    var img = el('img', { class: 'ff-viewer__img', alt: '' });
    var stage = el('div', { class: 'ff-viewer__stage' }, [img]);
    // Previous / next arrows and a strip of thumbnails, all overlaid on the image.
    var prevBtn = el('button', { type: 'button', class: 'ff-viewer__nav ff-viewer__nav--prev', 'aria-label': t.prevImage, title: t.prevImage },
      [el('span', { 'aria-hidden': 'true', text: '‹' })]);
    var nextBtn = el('button', { type: 'button', class: 'ff-viewer__nav ff-viewer__nav--next', 'aria-label': t.nextImage, title: t.nextImage },
      [el('span', { 'aria-hidden': 'true', text: '›' })]);
    var thumbs = el('div', { class: 'ff-viewer__thumbs', role: 'group', 'aria-label': 'Product images' });
    var stageWrap = el('div', { class: 'ff-viewer__stage-wrap' }, [stage, prevBtn, nextBtn, thumbs]);
    var title = el('h2', { class: 'ff-viewer__title', id: titleId });
    var desc = el('div', { class: 'ff-viewer__desc' });
    var price = el('p', { class: 'ff-viewer__price' });
    var buyBox = el('div', { class: 'ff-viewer__buy' });
    // Desktop: title + description on the left, price + Add to cart on the right.
    // Mobile: stacked, with Add to cart below the description.
    var info = el('div', { class: 'ff-viewer__info' }, [
      el('div', { class: 'ff-viewer__text' }, [title, desc]),
      buyBox
    ]);
    var panel = el('div', { class: 'ff-viewer__panel', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': titleId }, [
      el('div', { class: 'ff-viewer__bar' }, [count, zoomOut, level, zoomIn, closeBtn]),
      stageWrap,
      info
    ]);
    var overlay = el('div', { class: 'ff-viewer', hidden: '' }, [panel]);
    overlay.hidden = true;

    var v = { overlay: overlay, panel: panel, stage: stage, img: img, title: title, desc: desc, info: info,
      price: price, buyBox: buyBox, count: count, prevBtn: prevBtn, nextBtn: nextBtn, thumbs: thumbs,
      gallery: [], index: 0,
      level: level, zoomIn: zoomIn, zoomOut: zoomOut, closeBtn: closeBtn, zoom: 0, baseWidth: 0, opener: null };

    zoomIn.addEventListener('click', function () { self.setZoom(v.zoom + 1); });
    zoomOut.addEventListener('click', function () { self.setZoom(v.zoom - 1); });
    closeBtn.addEventListener('click', function () { self.closeViewer(); });
    prevBtn.addEventListener('click', function () { self.showImage(v.index - 1); });
    nextBtn.addEventListener('click', function () { self.showImage(v.index + 1); });
    // Swipe left/right on phones (only when not zoomed in, so panning still works).
    var touchX = null;
    stage.addEventListener('touchstart', function (e) {
      touchX = (v.zoom === 0 && e.touches.length === 1) ? e.touches[0].clientX : null;
    }, { passive: true });
    stage.addEventListener('touchend', function (e) {
      if (touchX === null || !e.changedTouches.length) return;
      var dx = e.changedTouches[0].clientX - touchX;
      touchX = null;
      if (Math.abs(dx) > 50) self.showImage(v.index + (dx < 0 ? 1 : -1));
    });
    // Click the image to step through zoom levels; click the dark backdrop to close.
    img.addEventListener('click', function () { self.setZoom(v.zoom < ZOOM_STEPS.length - 1 ? v.zoom + 1 : 0); });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) self.closeViewer(); });
    // Listen on the document so keys still work after clicking the image.
    document.addEventListener('keydown', function (e) {
      if (overlay.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); self.closeViewer(); }
      else if (e.key === '+' || e.key === '=') { e.preventDefault(); self.setZoom(v.zoom + 1); }
      else if (e.key === '-') { e.preventDefault(); self.setZoom(v.zoom - 1); }
      else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && v.gallery.length > 1 &&
               !/^(SELECT|INPUT)$/.test((e.target && e.target.tagName) || '')) {
        // Arrow keys move through images (but still work normally inside the option picker).
        e.preventDefault();
        self.showImage(v.index + (e.key === 'ArrowRight' ? 1 : -1));
      }
      else if (e.key === 'Tab') {
        // Keep keyboard focus inside the viewer.
        var f = [].slice.call(panel.querySelectorAll('button, select'))
          .filter(function (b) { return !b.disabled && !b.hidden && !(b.parentNode && b.parentNode.hidden); });
        var i = f.indexOf(document.activeElement);
        if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); f[0].focus(); }
      }
    });

    document.body.appendChild(overlay);
    this.viewer = v;
    return v;
  };

  FittingFinder.prototype.openViewer = function (p, opener, startIndex) {
    var self = this;
    var v = this.viewer || this.buildViewer();
    v.opener = opener || null;
    v.product = p;
    v.gallery = p.gallery && p.gallery.length ? p.gallery : [{ t: p.image, l: p.imageLarge || p.image, alt: p.title }];
    v.title.textContent = p.title;

    // Thumbnails (only when there is more than one image).
    v.thumbs.innerHTML = '';
    var many = v.gallery.length > 1;
    v.thumbs.hidden = !many;
    v.prevBtn.hidden = !many;
    v.nextBtn.hidden = !many;
    v.count.hidden = !many;
    if (many) {
      v.gallery.forEach(function (g, i) {
        var tb = el('button', { type: 'button', class: 'ff-viewer__thumb', 'aria-label': 'Image ' + (i + 1) + ' of ' + v.gallery.length },
          [el('img', { src: g.t, alt: '', loading: 'lazy', width: '80', height: '80' })]);
        tb.addEventListener('click', function () { self.showImage(i); });
        v.thumbs.appendChild(tb);
      });
    }
    this.showImage(startIndex || 0, true);

    var html = this.showDescription ? String(p.description || '').trim() : '';
    v.desc.innerHTML = html; // product description written in Shopify admin
    v.desc.hidden = !html;

    // Fresh Add to cart for this product. After a successful add the viewer
    // closes so the theme's cart popup / drawer is visible.
    v.buyBox.innerHTML = '';
    v.price.textContent = '';
    v.buyBox.appendChild(v.price);
    v.buyBox.appendChild(this.buildBuy(p, v.price, function () { self.closeViewer(); }));

    v.overlay.hidden = false;
    document.documentElement.classList.add('ff-viewer-open');
    this.setZoom(0);
    v.panel.scrollTop = 0;
    v.closeBtn.focus();
  };

  // Show image i of the open product (wraps around). Each new image starts at 100%.
  FittingFinder.prototype.showImage = function (i, silent) {
    var v = this.viewer;
    if (!v || !v.gallery.length) return;
    var n = v.gallery.length;
    i = ((i % n) + n) % n;
    v.index = i;
    var g = v.gallery[i];
    v.zoom = 0;
    v.baseWidth = 0;
    v.img.style.width = '';
    v.img.className = 'ff-viewer__img';
    v.img.alt = g.alt || (v.product && v.product.title) || '';
    v.img.src = g.l;
    v.count.textContent = (i + 1) + ' / ' + n;
    [].slice.call(v.thumbs.children).forEach(function (tb, k) {
      tb.classList[k === i ? 'add' : 'remove']('is-active');
      if (k === i) tb.setAttribute('aria-current', 'true'); else tb.removeAttribute('aria-current');
    });
    var active = v.thumbs.children[i];
    if (active && active.scrollIntoView && !silent) active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (!silent) this.setZoom(0);
  };

  FittingFinder.prototype.setZoom = function (step) {
    var v = this.viewer;
    if (!v) return;
    step = Math.max(0, Math.min(ZOOM_STEPS.length - 1, step));
    if (step > 0 && !v.baseWidth) v.baseWidth = fittedWidth(v);
    v.zoom = step;
    var z = ZOOM_STEPS[step];
    if (step === 0) {
      v.img.style.width = '';
      v.img.classList.remove('is-zoomed');
      v.baseWidth = 0;
    } else {
      v.img.classList.add('is-zoomed');
      v.img.style.width = Math.round(v.baseWidth * z) + 'px';
      // Keep the middle of the image in view.
      v.stage.scrollLeft = (v.stage.scrollWidth - v.stage.clientWidth) / 2;
      v.stage.scrollTop = (v.stage.scrollHeight - v.stage.clientHeight) / 2;
    }
    v.level.textContent = Math.round(z * 100) + '%';
    v.zoomOut.disabled = step === 0;
    v.zoomIn.disabled = step === ZOOM_STEPS.length - 1;
    // Don't strand keyboard focus on a button that just became disabled.
    var a = document.activeElement;
    if (a && a.disabled) (a === v.zoomIn ? v.zoomOut : v.zoomIn).focus();
  };

  FittingFinder.prototype.closeViewer = function () {
    var v = this.viewer;
    if (!v || v.overlay.hidden) return;
    v.overlay.hidden = true;
    v.img.removeAttribute('src');
    v.desc.innerHTML = '';
    v.buyBox.innerHTML = '';
    v.thumbs.innerHTML = '';
    document.documentElement.classList.remove('ff-viewer-open');
    if (v.opener && v.opener.focus) v.opener.focus();
  };

  /* ---------- boot ---------- */

  function init(scope) {
    (scope || document).querySelectorAll('[data-fitting-finder]').forEach(function (node) {
      if (node.__fittingFinder) return;
      node.__fittingFinder = new FittingFinder(node);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  window.FittingFinder = { init: init, _remainingEnds: remainingEnds, _norm: norm };
})();
